import React, { useState, useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { FiSearch, FiLoader } from 'react-icons/fi'
import { addMessage, getMessage } from '../../api/contactus'
import Pagination from '../../components/Pagination'
import { handleDate } from '../../handleDate'
import toast from 'react-hot-toast'

export default function Message() {
  const [searchTerm, setSearchTerm] = useState('')
  const [page, setPage] = useState(1)

  const queryClient = useQueryClient()

  // 🔹 Fetch data from API
  const { data, isPending, isError, error } = useQuery({
    queryKey: ['contactus', page],
    queryFn: () => getMessage(page),
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  })

  const message = data?.messages || []
  console.log(message)

  const totalPages = Math.ceil((data?.totalCount || 0) / 9)

  // 🔹 Search Filter
  const filteredData = useMemo(() => {
    return message.filter(
      (item) =>
        item.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.subject?.toLowerCase().includes(searchTerm.toLowerCase())
    )
  }, [searchTerm, message])

  const createMutation = useMutation({
    mutationFn: addMessage,
    onSuccess: () => {
      toast.success('Subjet Added')

      queryClient.invalidateQueries(['contactus'])
    },
    onError: (error) => {
      console.error('error:', error)
      toast.error(
        error?.response?.data?.message || 'Not Added, Try After sometime.'
      )
    },
  })

  // 🔹 Loading State
  if (isPending) {
    return (
      <div className="flex justify-center items-center h-80">
        <FiLoader className="w-8 h-8 animate-spin text-orange-500" />
        <span className="ml-3 text-gray-600 text-lg font-medium">
          Loading messages...
        </span>
      </div>
    )
  }

  // 🔹 Error State
  if (isError) {
    return (
      <div className="text-center text-red-600 mt-20">
        <p className="text-lg font-semibold">Failed to load data 😞</p>
        <p className="text-gray-500">{error?.message}</p>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header */}
      <h2 className="text-2xl font-aptos-semibold text-gray-800 text-center mb-6">
        🧾 User Messages
      </h2>

      {/* Search Bar */}
      <div className="max-w-md mx-auto mb-6 flex items-center bg-white border rounded-full shadow-sm px-4 py-2">
        <FiSearch className="text-gray-400 w-5 h-5 mr-2" />
        <input
          type="text"
          placeholder="Search by email or Title"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="border-none focus:ring-0 focus:outline-none w-full text-gray-700"
        />
      </div>

      {/* Data Grid */}
      <div className="grid md:grid-cols-2 gap-6">
        {filteredData?.length > 0 ? (
          filteredData?.map((item) => (
            <div
              key={item.id}
              className="bg-white border border-gray-200 rounded-2xl p-5"
            >
              <div className="flex justify-between items-center mb-3">
                <span
                  className={`px-2 py-1 text-sm rounded-full ${
                    item.subject === 'Founder'
                      ? 'bg-yellow-100 text-yellow-700'
                      : item.subject === 'Investor'
                      ? 'bg-green-100 text-green-700'
                      : 'bg-red-100 text-red-700'
                  }`}
                >
                  {item.subject}
                </span>
              </div>

              <p className="text-sm text-gray-500 mb-2">
                <strong>Name:</strong> {item.fullName}
              </p>
              <p className="text-sm text-gray-500 mb-2">
                <strong>Mobile:</strong> {item.mobile}
              </p>
              <p className="text-sm text-gray-500 mb-2">
                <strong>Email:</strong> {item.email}
              </p>
              <p className="text-sm text-gray-500 mb-2">
                <strong>Created At:</strong> {handleDate(item.createdAt)}
              </p>
              <div className="text-sm text-gray-500 mb-2">
                <strong>Message:</strong>
                <pre className="mt-1 bg-slate-50 text-gray-700 p-2 rounded-md overflow-x-auto whitespace-pre-wrap break-all text-sm">
                  {item.message}
                </pre>
              </div>
              {(item.subject === 'Investor' || item.subject === 'Founder') &&
                !item.added && (
                  <button
                    disabled={createMutation.isPending}
                    onClick={() => createMutation.mutate(item.id)}
                    className="bg-orange-700 text-orange-100 p-2 rounded shadow hover:text-orange-800 hover:bg-orange-200 hover:shadow-none transition-all duration-300"
                  >
                    Add this {item.subject}
                  </button>
                )}
            </div>
          ))
        ) : (
          <div className="col-span-full text-center text-gray-500 mt-10">
            No records found 😶
          </div>
        )}
      </div>

      {/* Pagination */}
      <div className="mt-8 flex justify-center">
        <Pagination page={page} setPage={setPage} totalPages={totalPages} />
      </div>
    </div>
  )
}
